import { Error } from "@/components/Error";
import { Checkbox } from "@/crud/form/Checkbox";
import { Input } from "@/crud/form/Input";
import { ExclamationCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FieldArray, useFormikContext } from "formik";
import { PlusIcon } from "lucide-react";

interface Props {
  fieldIndex: number;
}

export const AddChoiceOptions = ({ fieldIndex }: Props) => {
  const { values, errors, handleChange, handleBlur } = useFormikContext<any>();

  return (
    <div className="mt-4">
      <p className="block text-sm font-medium leading-6 text-gray-900 dark:text-white mb-3">
        Choices
      </p>

      {errors.fields && errors.fields[fieldIndex]?.options && (
        <div className="mt-1 mb-3 text-red-500 text-sm flex items-center border border-red-500 rounded-md p-1.5">
          <ExclamationCircleIcon className="h-5 w-5 mr-1" />
          {typeof errors.fields[fieldIndex].options === "string" ? (
            // Render the error if it's a string
            <p>{errors.fields[fieldIndex].options}</p>
          ) : (
            // Render the error if it's an array of objects
            <ul className="list-disc pl-5">
              {errors.fields[fieldIndex].options.map(
                (error: any, index: number) => (
                  <>{error && <li key={index}>{error.name}</li>}</>
                )
              )}
            </ul>
          )}
        </div>
      )}

      <FieldArray name={`fields.${fieldIndex}.options`}>
        {({ insert, remove, push }) => (
          <div className="mt-2">
            {values.fields[fieldIndex]?.options?.length > 0 ? (
              values.fields[fieldIndex].options.map(
                (option: any, index: number) => (
                  <div className="pb-2" key={index}>
                    <div className="flex">
                      <div className="dark:bg-white w-10 rounded-md border mr-2 flex items-center justify-center">
                        <h3 className="dark:text-dark-primary text-zinc-500 -mt-0.5">
                          {index + 1}
                        </h3>
                      </div>
                      <div className="flex-1">
                        <Input
                          required
                          type="text"
                          name={`fields.${fieldIndex}.options.${index}.name`}
                          value={option.name}
                          surpressError={true}
                          onChange={handleChange}
                        />
                      </div>
                      {values.mode.id === "quiz" && (
                        <div className="ml-2">
                          <Checkbox
                            name={`fields.${fieldIndex}.options.${index}.isCorrect`}
                            isChecked={option.isCorrect}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="dark:bg-white w-10 rounded-md border ml-2 flex items-center justify-center"
                      >
                        <TrashIcon className="h-5 w-5 dark:text-dark-primary text-zinc-500" />
                      </button>
                    </div>
                  </div>
                )
              )
            ) : (
              <div className="-mt-0.5">
                <p className="dark:text-gray-400 text-xs">No choices yet</p>
              </div>
            )}

            <button
              type="button"
              onClick={() => push({ name: "", isCorrect: false })}
              className="mt-2 dark:text-white border border-white/10 hover:bg-white/5 pr-3 pl-1.5 py-1 rounded-md text-xs flex"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add choice
            </button>
          </div>
        )}
      </FieldArray>
    </div>
  );
};
